import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, userApi } from '@/lib/api';

export interface User {
  id: number;
  email: string;
  username?: string;
  firstName: string;
  lastName: string;
  displayName: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
}

interface RegisterData {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = 'braceesapori_auth';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Carica utente salvato al mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        const savedAuth = localStorage.getItem(AUTH_STORAGE_KEY);
        if (savedAuth) {
          const { token } = JSON.parse(savedAuth);
          // Verifica che il token sia ancora valido
          const validation = await authApi.validateToken();
          if (validation.valid && validation.user) {
            const userData: User = {
              id: validation.user.id,
              email: validation.user.email,
              firstName: validation.user.first_name || '',
              lastName: validation.user.last_name || '',
              displayName: validation.user.display_name || validation.user.email,
            };
            setUser(userData);
          } else {
            // Token non valido, rimuovi
            localStorage.removeItem(AUTH_STORAGE_KEY);
          }
        }
      } catch (error) {
        console.error('Errore nel caricamento utente:', error);
        localStorage.removeItem(AUTH_STORAGE_KEY);
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const data = await authApi.login(username, password);

      if (!data.token || !data.user) {
        throw new Error('Risposta di autenticazione non valida');
      }

      // Salva token e dati utente
      const userData: User = {
        id: data.user.id,
        email: data.user.email,
        firstName: data.user.first_name || '',
        lastName: data.user.last_name || '',
        displayName: data.user.display_name || data.user.email,
      };

      setUser(userData);
      localStorage.setItem(
        AUTH_STORAGE_KEY,
        JSON.stringify({
          token: data.token,
          userId: data.user.id,
        })
      );
    } catch (error) {
      console.error('Errore nel login:', error);
      throw error;
    }
  };

  const register = async (data: RegisterData) => {
    try {
      const result = await authApi.register({
        email: data.email,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
      });

      if (!result.token || !result.user) {
        throw new Error('Errore nella registrazione');
      }

      // Salva token e dati utente
      const userData: User = {
        id: result.user.id,
        email: result.user.email,
        firstName: result.user.first_name || '',
        lastName: result.user.last_name || '',
        displayName: result.user.display_name || result.user.email,
      };

      setUser(userData);
      localStorage.setItem(
        AUTH_STORAGE_KEY,
        JSON.stringify({
          token: result.token,
          userId: result.user.id,
        })
      );
    } catch (error) {
      console.error('Errore nella registrazione:', error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

